// ResetPassword.js - Page indépendante de réinitialisation
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Shield, Check, X, AlertCircle } from 'lucide-react';
import './ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setMessage({ 
        type: 'error', 
        text: 'Token de réinitialisation manquant ou invalide' 
      });
    }
  }, [token]);

  // 🔒 MÊME LOGIQUE QUE Account.js pour la force du mot de passe
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength += 25;
    setPasswordStrength(strength);
    return strength;
  };

  const handlePasswordChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'newPassword') {
      calculatePasswordStrength(value);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getPasswordStrengthInfo = () => {
    if (passwordStrength < 25) return { color: '#ef4444', text: 'Très faible' };
    if (passwordStrength < 50) return { color: '#f59e0b', text: 'Faible' };
    if (passwordStrength < 75) return { color: '#10b981', text: 'Moyen' };
    return { color: '#059669', text: 'Fort' };
  };

  const passwordsMatch = formData.newPassword && formData.confirmPassword && 
                        formData.newPassword === formData.confirmPassword;

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!formData.newPassword || !formData.confirmPassword) {
    setMessage({ type: 'error', text: 'Veuillez remplir tous les champs' });
    return;
  }

  if (formData.newPassword !== formData.confirmPassword) {
    setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
    return;
  }

  if (passwordStrength < 75) {
    setMessage({ type: 'error', text: 'Le mot de passe n\'est pas assez fort (minimum 75%)' });
    return;
  }

  setLoading(true);
  try {
    // ✅ CORRIGÉ : URL complète avec localhost:5000
    const response = await fetch('http://localhost:5000/api/auth/confirm-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        password: formData.newPassword,
        confirmPassword: formData.confirmPassword
      })
    });

    const data = await response.json();

    if (data.success) {
      setMessage({ 
        type: 'success', 
        text: 'Mot de passe réinitialisé avec succès !' 
      });
      
      // Connexion automatique si token JWT fourni
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } else {
      setMessage({ type: 'error', text: data.message || 'Erreur lors de la réinitialisation' });
    }
  } catch (error) {
    setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
  } finally {
    setLoading(false);
  }
};

  const strengthInfo = getPasswordStrengthInfo();

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-header">
          <h1>🔒 Nouveau Mot de Passe</h1>
          <p>Définissez un nouveau mot de passe sécurisé pour votre compte</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label>
              <Lock size={16} />
              Nouveau mot de passe
            </label>
            <div className="password-input">
              <input
                type={showPasswords.new ? "text" : "password"}
                value={formData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="Entrez votre nouveau mot de passe"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
              >
                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            {formData.newPassword && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div 
                    className="strength-fill"
                    style={{ 
                      width: `${passwordStrength}%`,
                      backgroundColor: strengthInfo.color
                    }}
                  ></div>
                </div>
                <span style={{ color: strengthInfo.color }}>
                  {strengthInfo.text}
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>
              <Shield size={16} />
              Confirmer le nouveau mot de passe
            </label>
            <div className="password-input">
              <input
                type={showPasswords.confirm ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="Confirmez votre nouveau mot de passe"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            {formData.confirmPassword && (
              <div className="password-match">
                {passwordsMatch ? <Check size={14} /> : <X size={14} />}
                <span style={{ color: passwordsMatch ? '#10b981' : '#ef4444' }}>
                  {passwordsMatch ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas'}
                </span>
              </div>
            )}
          </div>

          <button 
            type="submit" 
            className="reset-button"
            disabled={loading || passwordStrength < 75 || !passwordsMatch}
          >
            {loading ? '⏳ Réinitialisation...' : '🔒 Réinitialiser le mot de passe'}
          </button>
        </form>

        <div className="back-to-login">
          <a href="/login">← Retour à la connexion</a>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;